import React, { useState } from "react";
import axios from "axios";
import BASE_URL from "../config"; // Import BASE_URL
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Import FontAwesomeIcon
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns"; // Import date-fns for date formatting
import ReactPaginate from "react-paginate"; // Import ReactPaginate for pagination
import "../styles/custom.css"; // Import custom CSS for pagination styling
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css"; // Import Toastify CSS
import Loader from "../components/Loader";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MMM dd, yyyy"); // Format as "Jan 25, 2019"
  } catch {
    return "Invalid Date";
  }
};

const Domains = () => {
  const [domains, setDomains] = useState([]); // Only keep the necessary state
  const [selectedRegistrar, setSelectedRegistrar] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [selectedRows, setSelectedRows] = useState([]); // State to track selected rows
  const [currentPage, setCurrentPage] = useState(0); // State for current page
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" }); // State for sorting configuration
  const [recordsPerPage, setRecordsPerPage] = useState(20); // Number of records per page
  const [csvUploading, setCsvUploading] = useState(false); // State for upload progress
  const [allDomains, setAllDomains] = useState([]); // Store all domains for "All" option
  const [loading, setLoading] = useState(false); // Loader state

  const handleRegistrarChange = async (e) => {
    const value = e.target.value;
    setSelectedRegistrar(value);
    setLoading(true); // Show loader
    setSelectedRows([]); // Clear selected rows on change

    if (value === "godaddy.com") {
      try {
        const response = await axios.get(`${BASE_URL}api/godaddy/domains`, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        setDomains(response.data);
      } catch (error) {
        console.error("Error fetching domains:", error);
        setDomains([]);
      } finally {
        setLoading(false);
      }
    } else if (value === "moniker") {
      try {
        const response = await axios.get(`${BASE_URL}api/moniker`, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        setDomains(response.data);
      } catch (error) {
        console.error("Error fetching moniker domains:", error);
        setDomains([]);
      } finally {
        setLoading(false);
      }
    } else if (value === "all") {
      try {
        // Fetch both godaddy and moniker data in parallel
        const [godaddyRes, monikerRes] = await Promise.all([
          axios.get(`${BASE_URL}api/godaddy/domains`, {
            headers: { "Content-Type": "application/json" },
          }),
          axios.get(`${BASE_URL}api/moniker`, {
            headers: { "Content-Type": "application/json" },
          }),
        ]);
        // Add a property to distinguish source for rendering
        const godaddyDomains = (godaddyRes.data || []).map(d => ({ ...d, _registrar: "godaddy.com" }));
        const monikerDomains = (monikerRes.data || []).map(d => ({ ...d, _registrar: "moniker" }));
        const combined = [...godaddyDomains, ...monikerDomains];
        setDomains(combined);
        setAllDomains(combined);
      } catch (error) {
        console.error("Error fetching all domains:", error);
        setDomains([]);
        setAllDomains([]);
      } finally {
        setLoading(false);
      }
    } else {
      setDomains([]);
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!", {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleRowSelection = (domain) => {
    setSelectedRows((prev) =>
      prev.includes(domain)
        ? prev.filter((item) => item !== domain) // Deselect if already selected
        : [...prev, domain] // Add to selected rows
    );
  };

  const copySelectedRows = () => {
    if (selectedRows.length === 0) {
      toast.error("No rows selected!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    const header = ["Domain", "Created At", "Expiry", "Status", "Auto Renew", "Renewable", "Registrar"];
    const rows = selectedRows.map((domain) => {
      const isMoniker = (selectedRegistrar === "moniker") || (selectedRegistrar === "all" && domain._registrar === "moniker");
      return [
        domain.domain || domain.Domain || "N/A",
        isMoniker
          ? domain.created_at
            ? formatDate(domain.created_at)
            : "N/A"
          : domain.createdAt
          ? formatDate(domain.createdAt)
          : "N/A",
        isMoniker
          ? domain.Expiration
            ? formatDate(domain.Expiration)
            : "N/A"
          : domain.expires
          ? formatDate(domain.expires)
          : "N/A",
        isMoniker
          ? domain.Status || "N/A"
          : domain.status || "N/A",
        isMoniker
          ? domain.Auto_Renew
            ? domain.Auto_Renew.toLowerCase() === "yes"
              ? "Yes"
              : domain.Auto_Renew
            : "N/A"
          : domain.renewAuto
          ? "Yes"
          : "No",
        isMoniker
          ? "N/A"
          : domain.renewable
          ? "Yes"
          : "No",
        getRegistrarName(domain),
      ];
    });

    // Calculate column widths for proper alignment
    const columnWidths = header.map((_, colIndex) =>
      Math.max(
        header[colIndex].length,
        ...rows.map((row) => row[colIndex].toString().length)
      )
    );

    // Format the table data with proper alignment
    const formattedTable = [
      header.map((col, index) => col.padEnd(columnWidths[index])).join("  "),
      ...rows.map((row) =>
        row.map((cell, index) => cell.toString().padEnd(columnWidths[index])).join("  ")
      ),
    ].join("\n");

    copyToClipboard(formattedTable);
  };

  // Utility to convert selected rows to CSV and trigger download
  const downloadSelectedRowsAsCSV = () => {
    if (selectedRows.length === 0) {
      toast.error("No rows selected!", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    const header = ["Domain", "Created At", "Expiry", "Status", "Auto Renew", "Renewable", "Registrar"];
    const rows = selectedRows.map((domain) => {
      const isMoniker = (selectedRegistrar === "moniker") || (selectedRegistrar === "all" && domain._registrar === "moniker");
      return [
        domain.domain || domain.Domain || "N/A",
        isMoniker
          ? domain.created_at
            ? formatDate(domain.created_at)
            : "N/A"
          : domain.createdAt
          ? formatDate(domain.createdAt)
          : "N/A",
        isMoniker
          ? domain.Expiration
            ? formatDate(domain.Expiration)
            : "N/A"
          : domain.expires
          ? formatDate(domain.expires)
          : "N/A",
        isMoniker
          ? domain.Status || "N/A"
          : domain.status || "N/A",
        isMoniker
          ? domain.Auto_Renew
            ? domain.Auto_Renew.toLowerCase() === "yes"
              ? "Yes"
              : domain.Auto_Renew
            : "N/A"
          : domain.renewAuto
          ? "Yes"
          : "No",
        isMoniker
          ? "N/A"
          : domain.renewable
          ? "Yes"
          : "No",
        getRegistrarName(domain),
      ];
    });
    // CSV escape utility
    const escapeCSV = (str) =>
      `"${String(str).replace(/"/g, '""')}"`;
    const csvContent =
      [header, ...rows]
        .map((row) => row.map(escapeCSV).join(","))
        .join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selected_domains.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected); // Update the current page
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Adjust filtering for all, godaddy, and moniker data
  const filteredDomains = domains.filter((domain) =>
    (domain.domain || domain.Domain || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  ); // Filter domains based on search query

  const sortedDomains = [...filteredDomains].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key] || "";
    const bValue = b[sortConfig.key] || "";
    if (sortConfig.direction === "asc") {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  // Update paginatedDomains calculation to handle "All"
  const paginatedDomains = recordsPerPage === "all"
    ? sortedDomains
    : sortedDomains.slice(
        currentPage * recordsPerPage,
        (currentPage + 1) * recordsPerPage
      ); // Slice domains for the current page

  // CSV upload handler for Moniker
  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        `${BASE_URL}api/moniker/upload-csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success(response.data.message || "CSV uploaded successfully!", {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.error || "CSV upload failed!",
        {
          position: "top-right",
          autoClose: 2000,
        }
      );
    } finally {
      setCsvUploading(false);
      e.target.value = ""; // Reset file input
    }
  };

  // For "All" option, determine registrar for each row
  const getRegistrarName = (domain) => {
    if (selectedRegistrar === "all") {
      if (domain._registrar === "godaddy.com") return "Godaddy.com";
      if (domain._registrar === "moniker") return "Moniker";
      return "N/A";
    }
    return selectedRegistrar
      ? selectedRegistrar.charAt(0).toUpperCase() + selectedRegistrar.slice(1)
      : "N/A";
  };

  return (
    <div className="container mt-4">
      {loading && <Loader message="Loading domains..." />}
      <h2>
        Domains
        <FontAwesomeIcon
          icon={faLink}
          className="ms-2 text-muted"
          style={{ cursor: "pointer", fontSize: "1rem" }} // Adjust font size for smaller icon
          title="Copy selected rows to clipboard"
          onClick={copySelectedRows} // Use the copySelectedRows function
        />
      </h2>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search domain..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} // Update search query
        />
      </div>
      <div className="mb-3 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <label htmlFor="registrarDropdown" className="form-label mb-0 me-2">
            Filter by Registrar
          </label>
          <select
            id="registrarDropdown"
            className="form-select"
            style={{ width: "auto", minWidth: 180 }}
            value={selectedRegistrar}
            onChange={handleRegistrarChange}
          >
            <option value="">Select Registrar</option>
            <option value="godaddy.com">godaddy.com</option>
            <option value="moniker">Moniker</option>
            <option value="all">All</option>
          </select>
        </div>
        <div style={{ minWidth: 260 }}>
          <label htmlFor="csvUpload" className="form-label mb-0 me-2">
            Upload Moniker CSV
          </label>
          <input
            id="csvUpload"
            type="file"
            accept=".csv"
            className="form-control d-inline-block"
            style={{ width: "auto", display: "inline-block" }}
            onChange={handleCsvUpload}
            disabled={csvUploading}
          />
          {csvUploading && (
            <div className="text-info mt-1">Uploading...</div>
          )}
        </div>
      </div>
      {/* Records per page dropdown */}
      <div className="mb-3 d-flex align-items-center">
        <label className="me-2 mb-0">Rows per page:</label>
        <select
          className="form-select"
          style={{ width: "auto", display: "inline-block" }}
          value={recordsPerPage}
          onChange={e => {
            const val = e.target.value;
            setRecordsPerPage(val === "all" ? "all" : parseInt(val, 10));
            setCurrentPage(0); // Reset to first page on change
          }}
        >
          <option value={20}>20</option>
          <option value={80}>80</option>
          <option value={150}>150</option>
          <option value="all">All</option>
        </select>
      </div>
      {/* Show Download CSV Button only if at least one row is selected */}
      {selectedRows.length > 0 && (
        <div className="mb-3">
          <button
            className="btn btn-success"
            onClick={downloadSelectedRowsAsCSV}
          >
            Download CSV
          </button>
        </div>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={(e) =>
                  setSelectedRows(
                    e.target.checked
                      ? paginatedDomains
                      : []
                  )
                }
                checked={
                  paginatedDomains.length > 0 &&
                  paginatedDomains.every((domain) => selectedRows.includes(domain))
                }
              />
            </th>
            <th onClick={() => handleSort("domain")} style={{ cursor: "pointer" }}>
              Domain {sortConfig.key === "domain" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
              Created At {sortConfig.key === "createdAt" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th onClick={() => handleSort("expires")} style={{ cursor: "pointer" }}>
              Expiry {sortConfig.key === "expires" && (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>Registrar</th>
            <th>Status</th>
            <th>Auto Renew</th>
            <th>Renewable</th>
          </tr>
        </thead>
        <tbody>
          {paginatedDomains.length > 0 ? (
            paginatedDomains.map((domain, index) => {
              // Support both GoDaddy and Moniker data structures and "All"
              const isMoniker = (selectedRegistrar === "moniker") || (selectedRegistrar === "all" && domain._registrar === "moniker");
              const domainName = domain.domain || domain.Domain || "N/A";
              const createdAt = isMoniker
                ? domain.created_at
                  ? formatDate(domain.created_at)
                  : "N/A"
                : domain.createdAt
                ? formatDate(domain.createdAt)
                : "N/A";
              const expiry = isMoniker
                ? domain.Expiration
                  ? formatDate(domain.Expiration)
                  : "N/A"
                : domain.expires
                ? formatDate(domain.expires)
                : "N/A";
              const registrar = getRegistrarName(domain);
              const status = isMoniker
                ? domain.Status || "N/A"
                : domain.status || "N/A";
              const autoRenew = isMoniker
                ? domain.Auto_Renew
                  ? domain.Auto_Renew.toLowerCase() === "yes"
                    ? "Yes"
                    : domain.Auto_Renew
                  : "N/A"
                : domain.renewAuto
                ? "Yes"
                : "No";
              const renewable = isMoniker
                ? "N/A"
                : domain.renewable
                ? "Yes"
                : "No";
              return (
                <tr key={index}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(domain)}
                      onChange={() => handleRowSelection(domain)}
                    />
                  </td>
                  <td>{domainName}</td>
                  <td>{createdAt}</td>
                  <td>{expiry}</td>
                  <td>{registrar}</td>
                  <td>{status}</td>
                  <td>{autoRenew}</td>
                  <td>{renewable}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="8" className="text-center">No data available</td>
            </tr>
          )}
        </tbody>
      </table>
      {/* Pagination controls, hide if "All" is selected */}
      {recordsPerPage !== "all" && (
        <ReactPaginate
          previousLabel={"Previous"}
          nextLabel={"Next"}
          pageCount={Math.ceil(filteredDomains.length / recordsPerPage)} // Calculate total pages
          onPageChange={handlePageChange}
          containerClassName={"pagination justify-content-center"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          activeLinkClassName={"active-link"} // Add custom class for active link
        />
      )}
      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
};

export default Domains;
